import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatPrice } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Download, Calendar, BarChart3, PackageOpen, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { normalizeNumberInput, sanitizeLeadingZeroInput } from "@/lib/utils";

export default function AdminReports() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("po-close");
  
  // States for PO Close Report
  const [poDate, setPoDate] = useState(new Date().toISOString().slice(0, 10));
  const [poData, setPoData] = useState([]);
  
  // States for Omset Summary
  const [omsetYear, setOmsetYear] = useState(new Date().getFullYear().toString());
  const [omsetData, setOmsetData] = useState([]);

  useEffect(() => {
    if (activeTab === "po-close") {
      API.get(`/admin/reports/po-close?date=${poDate}`).then(r => setPoData(r.data.report || [])).catch(()=>{});
    } else if (activeTab === "omset") {
      API.get(`/admin/reports/omset-summary?year=${omsetYear}`).then(r => setOmsetData(r.data.summary || [])).catch(()=>{});
    }
  }, [activeTab, poDate, omsetYear]);

  const handleExportPO = () => {
    window.open(`${API.defaults.baseURL}/admin/export/po-transactions`, '_blank');
  };

  return (
    <AdminLayout title={t("admin_reports.title")}>
      <div className="space-y-6">
        
        {/* Tabs */}
        <div className="flex border-b border-stone-200">
          <button 
            onClick={() => setActiveTab("po-close")}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'po-close' ? 'border-b-2 border-[#8A9A86] text-[#8A9A86]' : 'text-stone-500 hover:text-stone-700'}`}
          >
            <div className="flex items-center gap-2"><PackageOpen className="w-4 h-4" /> {t("admin_reports.tabs.po_close")}</div>
          </button>
          <button 
            onClick={() => setActiveTab("omset")}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'omset' ? 'border-b-2 border-[#8A9A86] text-[#8A9A86]' : 'text-stone-500 hover:text-stone-700'}`}
          >
            <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4" /> {t("admin_reports.tabs.omset")}</div>
          </button>
          <button 
            onClick={() => setActiveTab("export")}
            className={`px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'export' ? 'border-b-2 border-[#8A9A86] text-[#8A9A86]' : 'text-stone-500 hover:text-stone-700'}`}
          >
            <div className="flex items-center gap-2"><Download className="w-4 h-4" /> {t("admin_reports.tabs.export")}</div>
          </button>
        </div>

        {/* Tab Content: PO Close */}
        {activeTab === "po-close" && (
          <div className="bg-white border border-stone-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-serif text-lg text-stone-900">{t("admin_reports.po_close_title")}</h2>
                <p className="text-sm text-stone-500">{t("admin_reports.po_close_desc")}</p>
              </div>
              <div className="flex items-center gap-3">
                <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="w-40" />
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.product_name")}</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.total_qty")}</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.transactions")}</th>
                    <th className="text-right p-4 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.omset")}</th>
                  </tr>
                </thead>
                <tbody>
                  {poData.map((row) => (
                    <tr key={row.id} className="border-b border-stone-50">
                      <td className="p-4 font-medium">{row.name.includes(' by ') ? row.name : (row.brand_name ? `${row.name} by ${row.brand_name}` : row.name)}</td>
                      <td className="p-4 text-right">{row.total_qty || 0} pcs</td>
                      <td className="p-4 text-right text-stone-500">{row.total_transactions || 0}</td>
                      <td className="p-4 text-right font-medium text-emerald-600">{formatPrice(row.total_omzet || 0)}</td>
                    </tr>
                  ))}
                  {poData.length === 0 && (
                    <tr><td colSpan="4" className="p-8 text-center text-stone-400 italic">{t("admin_reports.no_data_po")}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab Content: Omset Summary */}
        {activeTab === "omset" && (
          <div className="bg-white border border-stone-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-serif text-lg text-stone-900">{t("admin_reports.omset_title")}</h2>
                <p className="text-sm text-stone-500">{t("admin_reports.omset_desc")}</p>
              </div>
              <div className="flex items-center gap-3">
                <Input type="number" value={omsetYear ?? ""} onChange={(e) => setOmsetYear(sanitizeLeadingZeroInput(e.target.value))} onBlur={(e) => setOmsetYear(String(normalizeNumberInput(e.target.value, new Date().getFullYear())))} className="w-24" />
              </div>
            </div>

            {omsetData.length > 0 ? (
              <div className="h-72 w-full mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={Object.values(omsetData.reduce((acc, curr) => {
                    if (!acc[curr.month]) acc[curr.month] = { month: curr.month, preorder: 0, ready_stock: 0 };
                    acc[curr.month][curr.order_type] = curr.omzet;
                    return acc;
                  }, {}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`} />
                    <Tooltip formatter={(v) => formatPrice(v)} />
                    <Legend />
                    <Bar dataKey="ready_stock" name={t("admin_reports.table.ready_stock")} stackId="a" fill="#8A9A86" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="preorder" name={t("admin_reports.table.preorder")} stackId="a" fill="#C2CABF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-72 flex items-center justify-center text-stone-400">{t("admin_reports.no_data_omset").replace("{year}", omsetYear)}</div>
            )}
            
            <div className="overflow-x-auto mt-8">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50/50">
                    <th className="text-left p-3 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.month")}</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.type")}</th>
                    <th className="text-right p-3 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.transactions")}</th>
                    <th className="text-right p-3 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.discount")}</th>
                    <th className="text-right p-3 text-xs font-semibold uppercase text-stone-500">{t("admin_reports.table.grand_total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {omsetData.map((row) => (
                    <tr key={`${row.month}-${row.order_type}`} className="border-b border-stone-50">
                      <td className="p-3 font-medium text-stone-800">{row.month}</td>
                      <td className="p-3">
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded font-bold ${row.order_type === 'preorder' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                          {row.order_type === 'preorder' ? t("admin_reports.table.preorder") : t("admin_reports.table.ready_stock")}
                        </span>
                      </td>
                      <td className="p-3 text-right">{row.total_transactions}</td>
                      <td className="p-3 text-right text-rose-500">{formatPrice(row.discount || 0)}</td>
                      <td className="p-3 text-right font-medium text-emerald-600">{formatPrice(row.omzet || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab Content: Export */}
        {activeTab === "export" && (
          <div className="bg-white border border-stone-100 p-8 text-center max-w-2xl mx-auto mt-8">
            <div className="w-16 h-16 bg-[#8A9A86]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Download className="w-8 h-8 text-[#8A9A86]" />
            </div>
            <h2 className="font-serif text-xl text-stone-900 mb-2">{t("admin_reports.export_title")}</h2>
            <p className="text-stone-500 mb-8 max-w-md mx-auto">{t("admin_reports.export_desc")}</p>
            
            <button 
              onClick={handleExportPO}
              className="bg-[#8A9A86] hover:bg-[#768573] text-white px-8 py-3 rounded text-sm font-medium tracking-wide transition-colors"
            >
              {t("admin_reports.download_btn")}
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
